import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import SimpleReactValidator from "simple-react-validator";
import { getIngredientStock, saveIngredientStock } from "../api/IngredientApi";
import { UseUoMHelper } from "../../../shared";
import { errorMessage, successMessage } from "../../../utils/helper";
import { ingredientKeys } from "./UseIngredientQueries";

const ingredientStockKeys = {
  all: ["ingredient-stock"],
  detail: (id) => [...ingredientStockKeys.all, id],
};

export const useIngredientStock = ({ optionsUnits = [], ingredient } = {}) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { getUniqueUnitIds } = UseUoMHelper();
  const [update, forceUpdate] = useState(false);
  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => message,
    }),
  );

  const [stockData, setStockData] = useImmer({
    data: {
      status: 0,
      stock_unit_id: "",
      stock_category_id: "",
      stock_subcategory_id: "",
      stock_tag_id: "",
      stock_code: "",
      stock_alt_unit_id: "",
      stocktake_display_name: "ingredient_name",
    },
    ingredientName: "",
    alternateName: null,
    isLoading: true,
    isEditing: false,
    isChanged: false,
  });

  // Fetch stock details
  const stockQuery = useQuery({
    queryKey: ingredientStockKeys.detail(id),
    queryFn: () => getIngredientStock(id),
    enabled: !!id,
  });

  // Calculate buyingUnits based on ingredient UoM (data passed from parent)
  const ingredientData = ingredient?.data;
  const unitIds = ingredientData?.units
    ? getUniqueUnitIds(ingredientData.units)
    : [];

  let buyingUnits = ingredientData?.has_uom
    ? optionsUnits?.filter((item) => unitIds.includes(item.id))
    : optionsUnits?.filter((item) => item?.name === "kg");

  buyingUnits = [...buyingUnits];
  const gmUnit = optionsUnits?.find((item) => item?.name === "gm");
  if (gmUnit) buyingUnits.push(gmUnit);

  // Populate state when stock data loads
  useEffect(() => {
    if (stockQuery.data) {
      const data = stockQuery.data;
      setStockData((draft) => {
        draft.data.status = data.status ?? 0;
        draft.data.stock_unit_id = data.stock_unit_id || "";
        draft.data.stock_alt_unit_id = data.stock_alt_unit_id || "";
        draft.data.stock_category_id = data.stock_category_id || "";
        draft.data.stock_subcategory_id = data.stock_subcategory_id || "";
        draft.data.stock_tag_id = data.stock_tag_id || "";
        draft.data.stock_code = data.stock_code || "";
        draft.data.stocktake_display_name =
          data.stocktake_display_name || "ingredient_name";
        draft.ingredientName = data.name || "";
        draft.alternateName = data.alternate_name;
        draft.isLoading = false;
      });
    }
  }, [stockQuery.data, setStockData]);

  // Handle loading state when no stock data yet (new ingredient)
  useEffect(() => {
    if (stockQuery.isError || (stockQuery.isFetched && !stockQuery.data)) {
      setStockData((draft) => {
        draft.isLoading = false;
      });
    }
  }, [stockQuery.isError, stockQuery.isFetched, stockQuery.data, setStockData]);

  // Save mutation
  const saveStock = useMutation({
    mutationFn: saveIngredientStock,
    onError: (error) => {
      errorMessage(
        error.response?.data?.message || "Failed to save stock details",
      );
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ingredientStockKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: ingredientKeys.detail(id),
      });
      setStockData((draft) => {
        draft.isEditing = false;
        draft.isChanged = false;
      });
      successMessage(res.message || "Stock details saved successfully");
    },
  });

  const onEdit = () => {
    setStockData((draft) => {
      draft.isEditing = true;
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setStockData((draft) => {
      draft.data[name] = value;
      draft.isChanged = true;
      // Clear subcategory when category changes
      if (name === "stock_category_id") {
        draft.data.stock_subcategory_id = undefined;
      }
      if (name === "stock_unit_id" && value === draft.data.stock_alt_unit_id) {
        draft.data.stock_alt_unit_id = null;
      }
    });
  };

  const onSubmit = () => {
    if (validator.current.allValid()) {
      const payload = {
        status: stockData.data.status,
        stock_unit_id: stockData.data.stock_unit_id || null,
        stock_category_id: stockData.data.stock_category_id || null,
        stock_subcategory_id: stockData.data.stock_subcategory_id || null,
        stock_tag_id: stockData.data.stock_tag_id || null,
        stock_code: stockData.data.stock_code || null,
        stocktake_display_name: stockData.data.stocktake_display_name || null,
        stock_alt_unit: stockData.data.stock_alt_unit_id || null,
      };
      saveStock.mutate({ id, payload });
    } else {
      validator.current.showMessages();
      forceUpdate(!update);
    }
  };

  return {
    stockData,
    onChange,
    onEdit,
    onSubmit,
    validator,
    isLoading: stockData.isLoading || stockQuery.isLoading,
    isSaving: saveStock.isPending,
    isEditing: stockData.isEditing,
    isChanged: stockData.isChanged,
    buyingUnits,
    alternateNameDisabled: stockData.alternateName == null,
  };
};
